import {RoleRepository} from './RoleRepository';
import {UserAPIDataSource} from '../api/UserAPIDataSource';
import {
  Role,
  CreateRoleRequest,
  RoleFilters,
  AssignRolePermissionRequest,
  BulkAssignRolePermissionsRequest
} from '../models/role.model';
import {AppError} from '../../../Data/config/AppError';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';
import {mapRolesListResponseToModel, mapRoleDTOToModel, mapCreateRoleRequestToDTO} from '../mappers/role.mapper';
import {mapPermissionDTOToModel} from '../mappers/permission.mapper';

export class RoleRepositoryImpl implements RoleRepository {
  private readonly userDataSource: UserAPIDataSource;

  constructor () {
    this.userDataSource = new UserAPIDataSource();
  }

  async createRole (roleData: CreateRoleRequest): Promise<Role> {
    try {
      const requestDTO = mapCreateRoleRequestToDTO(roleData);
      const response = await this.userDataSource.createRole(requestDTO);

      // Handle case where backend returns null data but success status
      if (!response || response === null) {
        // Create a minimal role object from the request data
        return {
          id: Date.now(), // Temporary ID
          name: roleData.name,
          displayName: roleData.displayName,
          description: roleData.description,
          permissions: roleData.permissions,
          userCount: 0,
          isSystem: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
      }

      return mapRoleDTOToModel(response);
    } catch (error) {
      // Re-throw error without additional handling to avoid duplicate notifications
      // The API layer already handled the error and showed notification
      throw error;
    }
  }

  async getRoles (filters?: RoleFilters): Promise<{ roles: Role[]; pagination: any }> {
    try {
      const defaultFilters = {
        page: 1,
        limit: PAGINATION_CONFIG.DEFAULT_PAGE_SIZE,
        ...filters
      };

      const response = await this.userDataSource.getRoles(defaultFilters);
      return mapRolesListResponseToModel(response);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  async getRoleById (id: number): Promise<Role> {
    try {
      const response = await this.userDataSource.getRoleById(id);
      return mapRoleDTOToModel(response);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }


  async deleteRole (id: number): Promise<void> {
    try {
      await this.userDataSource.deleteRole(id);
    } catch (error) {
      // Re-throw error without additional handling to avoid duplicate notifications
      // The API layer already handled the error and showed notification
      throw error;
    }
  }

  async assignRolePermission (data: AssignRolePermissionRequest): Promise<void> {
    try {
      await this.userDataSource.assignRolePermission(data);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  async bulkAssignRolePermissions (data: BulkAssignRolePermissionsRequest): Promise<void> {
    try {
      await this.userDataSource.bulkAssignRolePermissions(data);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  async getRolePermissions (roleId: number): Promise<any[]> {
    try {
      const response = await this.userDataSource.getRolePermissions(roleId);

      // Map the permissions from DTO to model format
      const mappedPermissions = response.map(permission => mapPermissionDTOToModel(permission));

      return mappedPermissions;
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  async removeRolePermission (roleId: number, permissionId: number): Promise<void> {
    try {
      await this.userDataSource.removeRolePermission(roleId, permissionId);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  async bulkRemoveRolePermissions (roleId: number, permissionIds: number[], reason?: string): Promise<void> {
    try {
      await this.userDataSource.bulkRemoveRolePermissions(roleId, permissionIds, reason);
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }

  async getRoleStatistics (): Promise<{
    total: number;
    systemRoles: number;
    customRoles: number;
    roleDistribution: Array<{ role: string; count: number }>;
  }> {
    try {
      const response = await this.userDataSource.getRoleStatistics();
      return response;
    } catch (error) {
      if (error instanceof AppError) {
        throw error;
      }
      throw AppError.fromApiError(error);
    }
  }
}
